/**
 * AddPermissionDialog Component
 * 
 * Dialog for adding new permission assignments to agents or collections.
 *
 * ISS-315: subject selector includes USERS and GROUPS with a searchable
 * filter; permission levels are aligned to the OPL standard of the entity
 * (admin/edit/operate/use) plus explicit deny — `view` was removed because
 * it maps to the same `users` relation as `use` in the Agent OPL.
 */

import { useMemo, useState } from 'react';
import { DiscardChangesDialog } from '@vanguard-atlas/shared';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  Button,
  Icon,
  Input,
} from '@/components/ui';
import { SimpleSelect } from '@/components/ui/form-wrappers';
import { useI18n } from '@/i18n';
import { sanitizePayload } from '@/lib/security';
import {
  useAvailableSubjects,
  useDenyPermission,
  useGrantPermission,
} from '../hooks/usePermissions';
import type { PermissionLevel, PermissionSubject } from '../types';

interface AddPermissionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: 'agent' | 'collection' | 'mcp_server';
  entityId: string;
}

const permissionLevels: PermissionLevel[] = ['admin', 'edit', 'operate', 'use', 'deny'];

export function AddPermissionDialog({
  open,
  onOpenChange,
  entityType,
  entityId,
}: AddPermissionDialogProps) {
  const { t } = useI18n();
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedPermission, setSelectedPermission] = useState<PermissionLevel>('use');
  const [subjectQuery, setSubjectQuery] = useState('');
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const isDirty = selectedSubject !== '' || selectedPermission !== 'use';
  const discardLabels = {
    title: t('common.discardChanges.title') ?? 'Discard changes?',
    message: t('common.discardChanges.message') ?? 'You have unsaved changes. If you close now, they will be lost.',
    confirm: t('common.discardChanges.discard') ?? 'Discard',
    cancel: t('common.discardChanges.keepEditing') ?? 'Keep editing',
  };
  const handleClose = () => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    onOpenChange(false);
  };
  
  const { data: subjects, isLoading: loadingSubjects } = useAvailableSubjects();
  const grantPermission = useGrantPermission();
  const denyPermission = useDenyPermission();
  
  // Deny only applies to users (Keto denied_users/denied_editors).
  const eligibleSubjects = useMemo(() => {
    const filtered = (subjects || []).filter((s) => {
      if (selectedPermission === 'deny' && s.type !== 'user') return false;
      if (!subjectQuery.trim()) return true;
      const q = subjectQuery.trim().toLowerCase();
      return (
        s.name.toLowerCase().includes(q) ||
        (s.email ?? '').toLowerCase().includes(q)
      );
    });
    return filtered.slice(0, 50);
  }, [subjects, selectedPermission, subjectQuery]);
  
  const handleSubjectSelect = (subject: PermissionSubject) => {
    setSelectedSubject(subject.id);
    setSubjectQuery(subject.email || subject.name);
  };
  
  const handleSubmit = async () => {
    if (!selectedSubject) return;
    
    const subject = subjects?.find(s => s.id === selectedSubject);
    if (!subject) return;
    
    const payload = sanitizePayload({
      entityType,
      entityId,
      subjectId: subject.id,
      subjectType: subject.type,
      permission: selectedPermission,
    });
    
    if (selectedPermission === 'deny') {
      // Explicit deny: backend uses denied_users; deny the `use` level
      // (blocks use/view/edit per the Agent OPL).
      await denyPermission.mutateAsync({
        entityType,
        entityId,
        subjectId: subject.id,
        subjectType: subject.type,
        level: 'use',
      });
    } else {
      await grantPermission.mutateAsync(payload);
    }
    
    // Reset and close
    setSelectedSubject('');
    setSelectedPermission('use');
    setSubjectQuery('');
    onOpenChange(false);
  };
  
  const permissionOptions = permissionLevels.map(level => ({
    value: level,
    label: t(`admin.permissions.levels.${level}`) || level,
  }));
  
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) { handleClose(); } }}>
      <DialogContent
        className="w-[calc(100vw-2rem)] max-w-md overflow-hidden"
        dirty={isDirty}
        discardTitle={discardLabels.title}
        discardMessage={discardLabels.message}
        discardConfirmLabel={discardLabels.confirm}
        discardCancelLabel={discardLabels.cancel}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon name="shield" size={20} className="text-blue-500" />
            {t('admin.permissions.addDialog.title')}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-1.5">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('admin.permissions.subject')}
            </span>
            <Input
              value={subjectQuery}
              onChange={(e) => {
                setSubjectQuery(e.target.value);
                setSelectedSubject('');
              }}
              placeholder={t('admin.permissions.addDialog.searchSubject')}
              disabled={loadingSubjects}
            />
            <div className="max-h-48 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
              {eligibleSubjects.length === 0 && (
                <p className="px-3 py-2 text-sm text-gray-500">
                  {t('admin.permissions.addDialog.noMatches')}
                </p>
              )}
              {eligibleSubjects.map((subject) => {
                const isSelected = subject.id === selectedSubject;
                return (
                  <button
                    key={`${subject.type}-${subject.id}`}
                    type="button"
                    onClick={() => handleSubjectSelect(subject)}
                    className={`flex w-full min-w-0 items-center px-3 py-2 text-left text-sm transition-colors ${
                      isSelected
                        ? 'bg-blue-50 dark:bg-blue-900/30'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium text-gray-900 dark:text-gray-100">
                        {subject.name}
                      </div>
                      <div className="truncate text-xs text-gray-500">
                        {subject.type === 'user'
                          ? subject.email || subject.name
                          : t('admin.permissions.addDialog.groupLabel')}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          
          <SimpleSelect
            className="w-full"
            label={t('admin.permissions.level')}
            options={permissionOptions}
            value={selectedPermission}
            onValueChange={(val) => {
              setSelectedPermission(val as PermissionLevel);
              setSelectedSubject('');
            }}
            required
          />
        </div>
        
        <DialogFooter className="w-full">
          <Button variant="outline" className="w-full sm:w-auto" onClick={handleClose}>
            {t('admin.permissions.addDialog.cancel')}
          </Button>
          <Button className="w-full sm:w-auto" onClick={handleSubmit} disabled={!selectedSubject || loadingSubjects}>
            {t('admin.permissions.addDialog.add')}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* ADR-114 enmienda 1: confirmación de descarte */}
      <DiscardChangesDialog
        open={confirmDiscardOpen}
        onDiscard={() => {
          setConfirmDiscardOpen(false);
          onOpenChange(false);
        }}
        onCancel={() => setConfirmDiscardOpen(false)}
        title={discardLabels.title}
        message={discardLabels.message}
        confirmLabel={discardLabels.confirm}
        cancelLabel={discardLabels.cancel}
      />
    </Dialog>
  );
}
